import React, { useCallback, useState, useMemo } from 'react';
import { DatePicker, TimePicker } from '@atlaskit/datetime-picker';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import availableTimes from '@atlassian/jira-common-constants/src/datetimepicker-available-times.tsx';
import {
	getTimePlaceholder,
	parseDateTimeToISO,
	parseDateValue,
	parseTimeValue,
} from './utils.tsx';

type Props = {
	locale: string;
	isInvalid: boolean;
	onChange: (value: string) => void;
	value: string;
	placeholder: string;
	dateLabel: string;
	timeLabel: string;
	testId?: string;
	defaultTime?: string;
};

export const DateTimePicker = ({
	locale,
	isInvalid = false,
	onChange,
	value,
	placeholder,
	dateLabel,
	timeLabel,
	testId,
	defaultTime = '00:00',
}: Props): React.JSX.Element => {
	const [internalDateValue, setInternalDateValue] = useState(() =>
		value ? parseDateValue(value) : '',
	);
	const [internalTimeValue, setInternalTimeValue] = useState(() =>
		value ? parseTimeValue(value) : '',
	);

	const handleOnDateChange = useCallback(
		(dateValue: string) => {
			setInternalDateValue(dateValue);

			const parsedDateTime = dateValue
				? parseDateTimeToISO(dateValue, internalTimeValue || defaultTime)
				: '';

			onChange(parsedDateTime);
		},
		[defaultTime, internalTimeValue, onChange],
	);

	const handleOnTimeChange = useCallback(
		(timeValue: string) => {
			setInternalTimeValue(timeValue);

			if (internalDateValue) {
				const parsedDateTime = parseDateTimeToISO(internalDateValue, timeValue || defaultTime);

				onChange(parsedDateTime);
			}
		},
		[defaultTime, internalDateValue, onChange],
	);
	const timePlaceholder = useMemo(() => {
		return getTimePlaceholder(defaultTime, locale);
	}, [defaultTime, locale]);

	return (
		<Inline xcss={baseContainerStyles} testId={testId}>
			<Box xcss={datePickerContainerStyles}>
				<DatePicker
					locale={locale}
					isInvalid={isInvalid}
					onChange={handleOnDateChange}
					placeholder={placeholder}
					label={dateLabel}
					value={internalDateValue}
					shouldShowCalendarButton
					testId={`${testId}--datepicker`}
				/>
			</Box>
			<Box xcss={timePickerContainerStyles}>
				<TimePicker
					locale={locale}
					isInvalid={isInvalid}
					onChange={handleOnTimeChange}
					label={timeLabel}
					value={internalTimeValue}
					timeIsEditable
					times={availableTimes}
					testId={`${testId}--timepicker`}
					placeholder={timePlaceholder}
				/>
			</Box>
		</Inline>
	);
};

const datePickerContainerStyles = xcss({
	flexBasis: '50%',
	flexGrow: 1,
	flexShrink: 0,
});

const timePickerContainerStyles = xcss({
	flexBasis: '50%',
	flexGrow: 1,
});

const baseContainerStyles = xcss({
	display: 'flex',
	gap: 'space.050',
});
